import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, 
  User, 
  Building2, 
  Bell, 
  Shield, 
  CreditCard,
  Mail,
  Phone,
  MapPin,
  Edit2,
  Check,
  X,
  Camera,
  FileText,
  Globe,
  Clock,
  Upload,
  Download,
  Eye,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import ChangePasswordModal from '@/components/shared/ChangePasswordModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

export default function BrokerSettings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    reraNumber: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    gstNumber: '',
    panNumber: '',
    website: '',
    experience: ''
  });

  const [notifications, setNotifications] = useState({
    emailNewDeals: true,
    emailCommission: true,
    emailWeeklyReport: true,
    smsNewDeals: false,
    smsCommission: true,
    pushNotifications: true
  });

  const [bankDetails, setBankDetails] = useState({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountType: 'savings'
  });

  // Documents state
  const [documents, setDocuments] = useState({
    rera: { status: 'NOT_UPLOADED', fileName: null, uploadedAt: null, expiryDate: null },
    pan: { status: 'NOT_UPLOADED', number: null, verified: false },
    gst: { status: 'NOT_UPLOADED', number: null, fileName: null },
    aadhaar: { status: 'NOT_UPLOADED', maskedNumber: null, verified: false }
  });
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const reraInputRef = useRef(null);
  const gstInputRef = useRef(null);
  const [appRejectionReason, setAppRejectionReason] = useState(null);

  useEffect(() => {
    fetchBrokerProfile();
    fetchDocuments();
  }, []);

  const fetchBrokerProfile = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id) return;
      
      const response = await api.get(`/broker/profile/${user.id}`);
      const data = response.data;
      
      setProfile({
        fullName: data.fullName || data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        companyName: data.companyName || '',
        reraNumber: data.reraNumber || data.brokerProfile?.reraNumber || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        pincode: data.pincode || '',
        gstNumber: data.gstNumber || '',
        panNumber: data.panNumber || data.brokerProfile?.panNumber || '',
        website: data.website || '',
        experience: data.experience || ''
      });
      
      if (data.bankDetails) {
        setBankDetails(data.bankDetails);
      }
      
      if (data.notifications) {
        setNotifications(data.notifications);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setProfile(prev => ({
        ...prev,
        fullName: user.name || '',
        email: user.email || '',
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id) return;
      
      const response = await api.get(`/broker/${user.id}/documents`);
      const data = response.data;
      setDocuments(data);
      if (data.appRejectionReason) setAppRejectionReason(data.appRejectionReason);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await api.put(`/broker/profile/${user.id}`, profile);
      toast.success('Profile updated successfully');
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await api.put(`/broker/notifications/${user.id}`, notifications);
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  // NEW: Separate handler for bank details
  const handleSaveBankDetails = async () => {
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await api.put(`/broker/${user.id}/bank-details`, bankDetails);
      toast.success('Bank details saved successfully');
    } catch (error) {
      console.error('Error saving bank details:', error);
      toast.error('Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  // Document upload handler
  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;
    
    setUploadingDoc(docType);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', docType);
      
      await api.post(`/broker/${user.id}/documents/upload`, formData);
      
      toast.success(`${docType.toUpperCase()} document uploaded successfully`);
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    switch (status) {
      case 'VERIFIED':
        return { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: CheckCircle2 };
      case 'PENDING':
        return { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', icon: Clock };
      case 'EXPIRED':
        return { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: AlertTriangle };
      case 'REJECTED':
        return { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: X };
      default:
        return { bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400', icon: FileText };
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'company', label: 'Company Details', icon: Building2 },
    { id: 'bank', label: 'Bank Account', icon: CreditCard },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex">
        <BrokerSidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <div className="text-white/50">Loading settings...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] flex">
      <BrokerSidebar />

      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-light tracking-tight text-white mb-1">
            Settings
          </h1>
          <p className="text-white/50 text-sm">Manage your account and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Tabs */}
          <div className="lg:col-span-1">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-[#4ADE80]/10 text-[#4ADE80] border-l-2 border-l-[#4ADE80]'
                        : 'text-white/60 hover:bg-white/5 border-l-2 border-l-transparent'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#0A0A0A] border border-white/10 rounded-sm"
            >
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-medium text-white">Personal Information</h2>
                    {!editMode ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditMode(true)}
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditMode(false)}
                          className="border-white/20 text-white hover:bg-white/10"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveProfile}
                          disabled={saving}
                          className="bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Save
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Profile Photo */}
                  <div className="flex items-center gap-4 mb-8 pb-6 border-b border-white/10">
                    <div className="relative">
                      <div className="w-20 h-20 bg-[#4ADE80]/20 rounded-sm flex items-center justify-center">
                        <span className="text-2xl font-medium text-[#4ADE80]">
                          {profile.fullName?.charAt(0) || 'B'}
                        </span>
                      </div>
                      {editMode && (
                        <button className="absolute -bottom-1 -right-1 w-7 h-7 bg-[#4ADE80] rounded-full flex items-center justify-center">
                          <Camera className="w-4 h-4 text-black" />
                        </button>
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{profile.fullName || 'Broker Name'}</p>
                      <p className="text-sm text-white/50">{profile.email}</p>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Full Name</Label>
                      <Input
                        value={profile.fullName}
                        onChange={(e) => setProfile({ ...profile, fullName: e.target.value })}
                        disabled={!editMode}
                        className="mt-2 bg-[#030303] border-white/10 text-white disabled:opacity-60"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Email</Label>
                      <Input
                        type="email"
                        value={profile.email}
                        disabled={true}
                        className="mt-2 bg-[#030303] border-white/10 text-white disabled:opacity-60"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Phone</Label>
                      <Input
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        disabled={!editMode}
                        className="mt-2 bg-[#030303] border-white/10 text-white disabled:opacity-60"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Experience (Years)</Label>
                      <Input
                        value={profile.experience}
                        onChange={(e) => setProfile({ ...profile, experience: e.target.value })}
                        disabled={!editMode}
                        className="mt-2 bg-[#030303] border-white/10 text-white disabled:opacity-60"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Company Tab */}
              {activeTab === 'company' && (
                <div className="p-6">
                  <h2 className="text-lg font-medium text-white mb-6">Company Details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Company Name</Label>
                      <Input
                        value={profile.companyName}
                        onChange={(e) => setProfile({ ...profile, companyName: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">RERA Number</Label>
                      <Input
                        value={profile.reraNumber}
                        onChange={(e) => setProfile({ ...profile, reraNumber: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">GST Number</Label>
                      <Input
                        value={profile.gstNumber}
                        onChange={(e) => setProfile({ ...profile, gstNumber: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">PAN Number</Label>
                      <Input
                        value={profile.panNumber}
                        onChange={(e) => setProfile({ ...profile, panNumber: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Website</Label>
                      <Input
                        value={profile.website}
                        onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                        placeholder="https://..."
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Business Address</Label>
                      <Input
                        value={profile.address}
                        onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">City</Label>
                      <Input
                        value={profile.city}
                        onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Pincode</Label>
                      <Input
                        value={profile.pincode}
                        onChange={(e) => setProfile({ ...profile, pincode: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="mt-6 bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}

              {/* Bank Tab */}
              {activeTab === 'bank' && (
                <div className="p-6">
                  <h2 className="text-lg font-medium text-white mb-6">Bank Account Details</h2>
                  <p className="text-white/50 text-sm mb-6">
                    Your commission payouts will be credited to this account.
                  </p>
                  
                  <div className="bg-[#030303] border border-white/10 rounded-sm p-4 mb-6">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-[#4ADE80]" />
                      <div>
                        <p className="text-white font-medium">{bankDetails.bankName || 'No bank added'}</p>
                        <p className="text-sm text-white/50">
                          {bankDetails.accountNumber ? `•••• •••• ${bankDetails.accountNumber.slice(-4)}` : 'Add your bank account'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Account Holder Name</Label>
                      <Input
                        value={bankDetails.accountHolderName}
                        onChange={(e) => setBankDetails({ ...bankDetails, accountHolderName: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Bank Name</Label>
                      <Input
                        value={bankDetails.bankName}
                        onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Account Number</Label>
                      <Input
                        value={bankDetails.accountNumber}
                        onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider">IFSC Code</Label>
                      <Input
                        value={bankDetails.ifscCode}
                        onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value })}
                        className="mt-2 bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleSaveBankDetails}
                    disabled={saving}
                    className="mt-6 bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
                    data-testid="save-bank-btn"
                  >
                    {saving ? 'Saving...' : 'Save Bank Details'}
                  </Button>
                </div>
              )}

              {/* Documents Tab - NEW */}
              {activeTab === 'documents' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-medium text-white">My Documents</h2>
                      <p className="text-white/50 text-sm mt-1">View and manage your KYC documents</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchDocuments}
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {/* Application-level rejection banner */}
                    {appRejectionReason && (
                      <div className="p-4 border border-red-500/20 bg-red-500/5 flex items-start gap-3" data-testid="app-rejection-banner">
                        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-red-400 font-medium">Application Rejected</p>
                          <p className="text-xs text-red-400/70 mt-1">{appRejectionReason}</p>
                        </div>
                      </div>
                    )}

                    {/* Hidden file inputs - always rendered */}
                    <input
                      type="file"
                      ref={reraInputRef}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      data-testid="rera-file-input"
                      onChange={(e) => { handleDocumentUpload('rera', e.target.files?.[0]); e.target.value = ''; }}
                    />
                    <input
                      type="file"
                      ref={gstInputRef}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      data-testid="gst-file-input"
                      onChange={(e) => { handleDocumentUpload('gst', e.target.files?.[0]); e.target.value = ''; }}
                    />

                    {/* RERA Certificate */}
                    <div className="bg-[#030303] border border-white/10 rounded-sm p-5" data-testid="rera-doc-section">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-500/10 rounded-sm flex items-center justify-center">
                            <FileText className="w-5 h-5 text-purple-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">RERA Certificate</h3>
                            <p className="text-xs text-white/40">Real Estate Regulatory Authority registration</p>
                          </div>
                        </div>
                        {documents.rera?.status && (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono rounded-sm border ${getStatusBadge(documents.rera.status).bg} ${getStatusBadge(documents.rera.status).border} ${getStatusBadge(documents.rera.status).text}`}>
                            {(() => {
                              const StatusIcon = getStatusBadge(documents.rera.status).icon;
                              return <StatusIcon className="w-3 h-3" />;
                            })()}
                            {documents.rera.status}
                          </span>
                        )}
                      </div>
                      
                      {/* RERA rejection reason */}
                      {documents.rera?.status === 'REJECTED' && documents.rera?.rejectionReason && (
                        <div className="mb-3 p-3 border border-red-500/20 bg-red-500/5 text-xs text-red-400 flex items-start gap-2" data-testid="rera-rejection-reason">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium">Rejected</p>
                            <p className="text-red-400/70 mt-0.5">{documents.rera.rejectionReason}</p>
                            <p className="text-red-400/50 mt-1">Please re-upload a valid document.</p>
                          </div>
                        </div>
                      )}

                      {documents.rera?.fileName ? (
                        <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-white">{documents.rera.fileName}</p>
                              <div className="flex items-center gap-4 mt-1 text-xs text-white/40">
                                {documents.rera.reraNumber && (
                                  <span>RERA: {documents.rera.reraNumber}</span>
                                )}
                                {documents.rera.fileSize && (
                                  <span>{documents.rera.fileSize}</span>
                                )}
                                {documents.rera.uploadedAt && (
                                  <span>Uploaded: {new Date(documents.rera.uploadedAt).toLocaleDateString()}</span>
                                )}
                              </div>
                              {documents.rera.expiryDate && (
                                <div className="flex items-center gap-1 mt-2 text-xs">
                                  <Clock className="w-3 h-3 text-white/40" />
                                  <span className={`${new Date(documents.rera.expiryDate) < new Date(Date.now() + 30*24*60*60*1000) ? 'text-yellow-400' : 'text-white/40'}`}>
                                    Expires: {new Date(documents.rera.expiryDate).toLocaleDateString()}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="border-white/20 text-white hover:bg-white/10"
                                data-testid="rera-replace-btn"
                                onClick={() => reraInputRef.current?.click()}
                                disabled={uploadingDoc === 'rera'}
                              >
                                {uploadingDoc === 'rera' ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4 mr-1" />
                                )}
                                Replace
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="border border-dashed border-white/20 rounded-sm p-6 text-center">
                          <Upload className="w-8 h-8 text-white/30 mx-auto mb-2" />
                          <p className="text-white/50 text-sm">No RERA certificate uploaded</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-3 border-white/20 text-white hover:bg-white/10"
                            data-testid="rera-upload-btn"
                            onClick={() => reraInputRef.current?.click()}
                            disabled={uploadingDoc === 'rera'}
                          >
                            {uploadingDoc === 'rera' ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-1" />
                            )}
                            Upload Certificate
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* PAN Card */}
                    <div className="bg-[#030303] border border-white/10 rounded-sm p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">PAN Card</h3>
                            <p className="text-xs text-white/40">Permanent Account Number</p>
                          </div>
                        </div>
                        {documents.pan?.status && (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono rounded-sm border ${getStatusBadge(documents.pan.status).bg} ${getStatusBadge(documents.pan.status).border} ${getStatusBadge(documents.pan.status).text}`}>
                            {(() => {
                              const StatusIcon = getStatusBadge(documents.pan.status).icon;
                              return <StatusIcon className="w-3 h-3" />;
                            })()}
                            {documents.pan.status}
                          </span>
                        )}
                      </div>
                      
                      <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-mono text-white tracking-wider">
                              {documents.pan?.number || profile.panNumber || 'Not provided'}
                            </p>
                            {documents.pan?.verifiedAt && (
                              <p className="text-xs text-white/40 mt-1">
                                Verified on {new Date(documents.pan.verifiedAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          {documents.pan?.verified && (
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* GST Certificate */}
                    <div className="bg-[#030303] border border-white/10 rounded-sm p-5" data-testid="gst-doc-section">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-500/10 rounded-sm flex items-center justify-center">
                            <FileText className="w-5 h-5 text-orange-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">GST Certificate</h3>
                            <p className="text-xs text-white/40">Goods and Services Tax registration</p>
                          </div>
                        </div>
                        {documents.gst?.status && documents.gst.status !== 'NOT_UPLOADED' && (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono rounded-sm border ${getStatusBadge(documents.gst.status).bg} ${getStatusBadge(documents.gst.status).border} ${getStatusBadge(documents.gst.status).text}`}>
                            {(() => {
                              const StatusIcon = getStatusBadge(documents.gst.status).icon;
                              return <StatusIcon className="w-3 h-3" />;
                            })()}
                            {documents.gst.status}
                          </span>
                        )}
                      </div>
                      
                      {/* GST rejection reason */}
                      {documents.gst?.status === 'REJECTED' && documents.gst?.rejectionReason && (
                        <div className="mb-3 p-3 border border-red-500/20 bg-red-500/5 text-xs text-red-400 flex items-start gap-2" data-testid="gst-rejection-reason">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium">Rejected</p>
                            <p className="text-red-400/70 mt-0.5">{documents.gst.rejectionReason}</p>
                            <p className="text-red-400/50 mt-1">Please re-upload a valid document.</p>
                          </div>
                        </div>
                      )}

                      {documents.gst?.fileName ? (
                        <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              {documents.gst.number && (
                                <p className="text-lg font-mono text-white tracking-wider">{documents.gst.number}</p>
                              )}
                              <div className="flex items-center gap-4 mt-1 text-xs text-white/40">
                                <span>{documents.gst.fileName}</span>
                                {documents.gst.fileSize && <span>{documents.gst.fileSize}</span>}
                                {documents.gst.uploadedAt && (
                                  <span>Uploaded: {new Date(documents.gst.uploadedAt).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="border-white/20 text-white hover:bg-white/10"
                                data-testid="gst-replace-btn"
                                onClick={() => gstInputRef.current?.click()}
                                disabled={uploadingDoc === 'gst'}
                              >
                                {uploadingDoc === 'gst' ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4 mr-1" />
                                )}
                                Replace
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="border border-dashed border-white/20 rounded-sm p-6 text-center">
                          <p className="text-white/50 text-sm">GST certificate not uploaded (optional)</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-3 border-white/20 text-white hover:bg-white/10"
                            data-testid="gst-upload-btn"
                            onClick={() => gstInputRef.current?.click()}
                            disabled={uploadingDoc === 'gst'}
                          >
                            {uploadingDoc === 'gst' ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-1" />
                            )}
                            Upload Certificate
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Aadhaar */}
                    <div className="bg-[#030303] border border-white/10 rounded-sm p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-cyan-500/10 rounded-sm flex items-center justify-center">
                            <User className="w-5 h-5 text-cyan-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">Aadhaar</h3>
                            <p className="text-xs text-white/40">Unique Identification Number</p>
                          </div>
                        </div>
                        {documents.aadhaar?.status && (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono rounded-sm border ${getStatusBadge(documents.aadhaar.status).bg} ${getStatusBadge(documents.aadhaar.status).border} ${getStatusBadge(documents.aadhaar.status).text}`}>
                            {(() => {
                              const StatusIcon = getStatusBadge(documents.aadhaar.status).icon;
                              return <StatusIcon className="w-3 h-3" />;
                            })()}
                            {documents.aadhaar.status}
                          </span>
                        )}
                      </div>
                      
                      <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-mono text-white tracking-wider">
                              {documents.aadhaar?.maskedNumber || '••••••••••••'}
                            </p>
                            {documents.aadhaar?.verifiedAt && (
                              <p className="text-xs text-white/40 mt-1">
                                Verified on {new Date(documents.aadhaar.verifiedAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          {documents.aadhaar?.verified && (
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="p-6">
                  <h2 className="text-lg font-medium text-white mb-6">Notification Preferences</h2>
                  
                  <div className="space-y-6">
                    <div className="border-b border-white/10 pb-6">
                      <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-white/50" />
                        Email Notifications
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm">New Deal Alerts</p>
                            <p className="text-white/40 text-xs">Get notified when new investment opportunities are listed</p>
                          </div>
                          <Switch
                            checked={notifications.emailNewDeals}
                            onCheckedChange={(checked) => setNotifications({ ...notifications, emailNewDeals: checked })}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm">Commission Updates</p>
                            <p className="text-white/40 text-xs">Receive updates when commission is credited</p>
                          </div>
                          <Switch
                            checked={notifications.emailCommission}
                            onCheckedChange={(checked) => setNotifications({ ...notifications, emailCommission: checked })}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm">Weekly Performance Report</p>
                            <p className="text-white/40 text-xs">Get a summary of your weekly performance</p>
                          </div>
                          <Switch
                            checked={notifications.emailWeeklyReport}
                            onCheckedChange={(checked) => setNotifications({ ...notifications, emailWeeklyReport: checked })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-b border-white/10 pb-6">
                      <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-white/50" />
                        SMS Notifications
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm">New Deal SMS</p>
                            <p className="text-white/40 text-xs">SMS alerts for new deals</p>
                          </div>
                          <Switch
                            checked={notifications.smsNewDeals}
                            onCheckedChange={(checked) => setNotifications({ ...notifications, smsNewDeals: checked })}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm">Commission SMS</p>
                            <p className="text-white/40 text-xs">SMS when commission is paid</p>
                          </div>
                          <Switch
                            checked={notifications.smsCommission}
                            onCheckedChange={(checked) => setNotifications({ ...notifications, smsCommission: checked })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveNotifications}
                    disabled={saving}
                    className="mt-6 bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
                  >
                    {saving ? 'Saving...' : 'Save Preferences'}
                  </Button>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="p-6">
                  <h2 className="text-lg font-medium text-white mb-6">Security Settings</h2>
                  
                  <div className="space-y-6">
                    <div className="bg-[#030303] border border-white/10 rounded-sm p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Change Password</p>
                          <p className="text-white/40 text-sm">Update your account password</p>
                        </div>
                        <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={() => setShowChangePassword(true)} data-testid="broker-change-pw-btn">
                          Update
                        </Button>
                      </div>
                    </div>

                    <div className="bg-[#030303] border border-white/10 rounded-sm p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Two-Factor Authentication</p>
                          <p className="text-white/40 text-sm">Add an extra layer of security</p>
                        </div>
                        <span className="px-3 py-1 bg-yellow-500/10 text-yellow-500 text-xs rounded-sm">
                          Coming Soon
                        </span>
                      </div>
                    </div>

                    <div className="bg-[#030303] border border-white/10 rounded-sm p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Login History</p>
                          <p className="text-white/40 text-sm">View your recent login activity</p>
                        </div>
                        <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                          View
                        </Button>
                      </div>
                    </div>

                    <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-red-400 font-medium">Delete Account</p>
                          <p className="text-white/40 text-sm">Permanently delete your broker account</p>
                        </div>
                        <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </main>
      <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </div>
  );
}
